'use client';

import React, {createRef, useState} from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import axios, {AxiosError} from 'axios';
import clsx from 'clsx';
import type {z} from 'zod';

import {Alert} from '@/modules/finkn/components/alert';
import {Button} from '@/modules/foundation/components/button';
import {Spinner} from '@/modules/foundation/components/spinner';

import {FormField, type ValidFieldNames} from '../form-field';

// import {SelectField} from '../select-field';
import {FormSchema} from './FormSchema';

export type FormData = z.infer<typeof FormSchema>;

const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
const recaptchaRef = createRef<ReCAPTCHA>();

export const Form = () => {
	const {
		register,
		handleSubmit,
		formState: {errors, isSubmitting},
		setError,
		setValue,
	} = useForm<FormData>({
		resolver: zodResolver(FormSchema),
	});

	const [responseMessage, setResponseMessage] = useState<string | null>(null);
	const [hasSubmitError, setHasSubmitError] = useState(false);

	const onSubmit = async (data: FormData) => {
		const recaptchaToken = await recaptchaRef.current?.executeAsync();

		try {
			setResponseMessage(null);
			setHasSubmitError(false);
			const response = await axios.post('/api/contact', data, {
				headers: {
					'Content-Type': 'application/json',
					'g-recaptcha-token': recaptchaToken ?? '',
					Accept: 'application/json',
				},
			});

			const {errors = {}} = response.data; // Destructure the 'errors' property from the response data

			// Define a mapping between server-side field names and their corresponding client-side names
			const fieldErrorMapping: Record<string, ValidFieldNames> = {
				name: 'name',
				email: 'email',
				message: 'message',
				phone: 'phone',
				// subject: 'subject',
			};

			// Find the first field with an error in the response data
			const fieldWithError = Object.keys(fieldErrorMapping)?.find(
				(field) => errors[field],
			);

			// If a field with an error is found, update the form error state using setError
			if (fieldWithError) {
				// Use the ValidFieldNames type to ensure the correct field names
				setError(fieldErrorMapping[fieldWithError], {
					type: 'server',
					message: errors[fieldWithError],
				});
			}

			// If the response status is 200 and there are no field errors, clear the form values
			if (response.status === 200 && !fieldWithError) {
				setValue('name', '');
				setValue('email', '');
				setValue('message', '');
				setValue('phone', '');
				// setValue('subject', '');
				setResponseMessage('Meldingen ble sendt!');
				setHasSubmitError(false);
			}
		} catch (error) {
			setResponseMessage('Noe gikk galt, prøv igjen senere');
			setHasSubmitError(true);
			if (error instanceof AxiosError) {
				console.error(error.response?.data.message);
			} else {
				console.error(error);
			}
		}
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<div className={clsx('col-auto', 'grid', 'gap-8')}>
				{/*<SelectField*/}
				{/*	name="subject"*/}
				{/*	label="Hva gjelder henvendelsen?"*/}
				{/*	register={register}*/}
				{/*	error={errors.subject}*/}
				{/*	required*/}
				{/*	options={[*/}
				{/*		{value: 'forsikring', label: 'Forsikring'},*/}
				{/*		{value: 'bank', label: 'Bank'},*/}
				{/*		{value: 'inkasso', label: 'Inkasso'},*/}
				{/*		{value: 'annet', label: 'Annet'},*/}
				{/*	]}*/}
				{/*/>*/}

				<FormField
					type="text"
					name="name"
					label="Navn"
					register={register}
					error={errors.name}
					required
				/>

				<FormField
					type="email"
					name="email"
					label="E-postadresse"
					register={register}
					error={errors.email}
					required
				/>

				<FormField
					type="tel"
					name="phone"
					label="Telefonnummer"
					register={register}
					error={errors.phone}
					required
				/>

				<FormField
					type="textarea"
					name="message"
					label="Melding"
					register={register}
					error={errors.message}
					required
				/>

				{responseMessage ? (
					<Alert
						title={responseMessage}
						severity={hasSubmitError ? 'error' : 'success'}
						role="status"
					/>
				) : null}

				<Button
					type="submit"
					disabled={isSubmitting}
					className={clsx('justify-self-start')}
				>
					Send kontaktskjema
					{isSubmitting && <Spinner title="sender" role="status" />}
				</Button>
			</div>
			{siteKey && (
				<ReCAPTCHA
					ref={recaptchaRef}
					sitekey={siteKey}
					size="invisible"
				/>
			)}
		</form>
	);
};
